import { useLazyQuery, useQuery } from '@apollo/client'

import { gql } from '@/clients/utoniq-core'
import {
  ArtistCampaignRankingFragmentDoc,
  CampaignArtistRankingQuery,
  CampaignArtistRankingQueryVariables,
  CampaignDetailFragmentDoc,
  CampaignDetailQuery,
  CampaignDetailQueryVariables,
  CampaignRankingType,
  CampaignUserRankingQuery,
  CampaignUserRankingQueryVariables,
  UserCampaignRankingFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ campaignId: string; artistId: string }>()
  const { analytics } = useAnalytics()
  const artistId = router.query.artistId ?? config.artistId
  const { data: campaignDetail, loading } = useQuery<
    CampaignDetailQuery,
    CampaignDetailQueryVariables
  >(QUERY, {
    variables: { code: router.query.campaignId },
  })

  const [
    getCampaignArtistRanking,
    { data: campaignArtistRanking, loading: campaignArtistRankGetting },
  ] = useLazyQuery<
    CampaignArtistRankingQuery,
    CampaignArtistRankingQueryVariables
  >(CAMPAIGN_ARTIST_RANKING, {
    variables: {
      inputs: {
        code: router.query.campaignId,
        type: CampaignRankingType.Artist,
        artistId,
      },
    },
    onCompleted({ campaignRanking }) {
      if (!campaignRanking?.ranker.length)
        analytics?.track('impression_noData', {
          applicationKey: config.applicationKey,
          campaignId: router.query.campaignId,
          name: 'campaignDetail',
        })
    },
  })

  const [
    getCampaignUserRanking,
    { data: campaignUserRanking, loading: campaignUserRankGetting },
  ] = useLazyQuery<CampaignUserRankingQuery, CampaignUserRankingQueryVariables>(
    CAMPAIGN_USER_RANKING,
    {
      variables: {
        inputs: {
          code: router.query.campaignId,
          type: CampaignRankingType.User,
          artistId,
        },
      },
      onCompleted({ campaignRanking }) {
        if (!campaignRanking?.ranker.length)
          analytics?.track('impression_noData', {
            applicationKey: config.applicationKey,
            campaignId: router.query.campaignId,
            name: 'campaignDetail',
          })
      },
    }
  )

  return {
    state: {
      campaign: campaignDetail?.campaign ?? null,
      campaignArtistRanking: campaignArtistRanking?.campaignRanking ?? null,
      campaignUserRanking: campaignUserRanking?.campaignRanking ?? null,
      loading,
      campaignArtistRankGetting,
      campaignUserRankGetting,
    },
    actions: {
      async onFetchCampaignRanking(rankingType) {
        if (rankingType === CampaignRankingType.User) {
          await getCampaignUserRanking({
            variables: {
              inputs: {
                code: router.query.campaignId,
                type: CampaignRankingType.User,
                artistId,
              },
            },
          })
          return
        }
        await getCampaignArtistRanking({
          variables: {
            inputs: {
              code: router.query.campaignId,
              type: CampaignRankingType.Artist,
              artistId,
            },
          },
        })
      },
      async onClickTab(isArtist) {
        if (isArtist) {
          await getCampaignUserRanking({
            variables: {
              inputs: {
                code: router.query.campaignId,
                type: CampaignRankingType.User,
                artistId,
              },
            },
          })
          return
        }
        await getCampaignArtistRanking({
          variables: {
            inputs: {
              code: router.query.campaignId,
              type: CampaignRankingType.Artist,
              artistId,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query CampaignDetail($code: String) {
    campaign(code: $code) {
      ...CampaignDetail
    }
  }
  ${CampaignDetailFragmentDoc}
`

const CAMPAIGN_USER_RANKING = gql`
  query CampaignUserRanking($inputs: CampaignRankingInput) {
    campaignRanking(inputs: $inputs) {
      ... on UserCampaignRanking {
        ...UserCampaignRanking
      }
    }
  }
  ${UserCampaignRankingFragmentDoc}
`

const CAMPAIGN_ARTIST_RANKING = gql`
  query CampaignArtistRanking($inputs: CampaignRankingInput) {
    campaignRanking(inputs: $inputs) {
      ... on ArtistCampaignRanking {
        ...ArtistCampaignRanking
      }
    }
  }
  ${ArtistCampaignRankingFragmentDoc}
`
